import {
  obtainAccessToken,
  generateAccountLoginUrl,
  removeAllCustomerRelatedData,
} from "./customerAccountApi";
import { trackAddToCart } from "../../helpers/dataLayer";

const RETURN_URL_KEY = "customer-account-return-url";
const LOGIN_ATTEMPTED_KEY = "customer-account-login-attempted";

const loginToCustomerAccountsApi = () => {
  // A failed or cancelled login returns without a code — without this guard
  // every page load would send the customer straight back out.
  if (sessionStorage.getItem(LOGIN_ATTEMPTED_KEY)) return;

  sessionStorage.setItem(LOGIN_ATTEMPTED_KEY, "true");
  sessionStorage.setItem(RETURN_URL_KEY, window.location.href);

  generateAccountLoginUrl.then((url) => {
    window.location = url;
  });
};

// The Customer Account API token only serves B2B pricing, so the Liquid
// storefront session decides who has one.
const obtainAccessTokenPromise = obtainAccessToken().then(
  ({ success, initialLogin }) => {
    if (initialLogin) {
      const returnUrl = sessionStorage.getItem(RETURN_URL_KEY);
      sessionStorage.removeItem(RETURN_URL_KEY);
      sessionStorage.removeItem(LOGIN_ATTEMPTED_KEY);

      // Logged in to the Customer Account API but not the Liquid storefront
      if (!window.theme.customer_id) {
        window.location = `${window.location.origin}/account/login`;
      } else if (returnUrl) {
        window.location = returnUrl;
      }

      return;
    }

    // B2B customer without a token
    if (!success) {
      if (window.theme.location_id) {
        loginToCustomerAccountsApi();
      }

      return;
    }

    // Logged out of the storefront — drop the token rather than signing back in
    if (!window.theme.customer_id) {
      removeAllCustomerRelatedData();
    }
  },
);

window.customerAccessTokenReady = obtainAccessTokenPromise;

/**
 * Change an item in the cart
 * @param {Object|Array} items [{ id, quantity }]
 * @param {Object} options object of callbacks
 */
window.changeCart = (change, options = {}) => {
  if (options?.onBeforeAdd) {
    options.onBeforeAdd();
  }

  const sections = [...document.querySelectorAll("[cart-section-id]")].map(
    (el) => el.getAttribute("cart-section-id"),
  );

  fetch("/cart/change.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ ...change, sections: sections.join() }),
  })
    .then((response) => response.json())
    .then((response) => {
      if (response.status === 422) {
        if (options?.onError) {
          options.onError(response);
        }

        return;
      }

      const updateCart = new CustomEvent("updateCart", {
        detail: {
          cart: response,
          data: options.data,
        },
      });
      document.dispatchEvent(updateCart);

      if (options?.onAfterAdd) {
        options.onAfterAdd(response);
      }
    })
    .catch((error) => {
      if (options?.onError) {
        options.onError(error);
      }

      console.error("Cart action failed:", error);
    });
};

/**
 * Remove an item from the cart
 * @param {Object|Array} items [{ id, quantity }]
 * @param {Object} options object of callbacks
 */
window.updateCart = (updates, options = {}) => {
  if (options?.onBeforeAdd) {
    options.onBeforeAdd();
  }

  const sections = [...document.querySelectorAll("[cart-section-id]")].map(
    (el) => el.getAttribute("cart-section-id"),
  );

  fetch("/cart/update.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ ...updates, sections: sections.join() }),
  })
    .then((response) => response.json())
    .then((response) => {
      if (response.status === 422 && response.message === "Cart Error") {
        if (options?.onError) {
          options.onError(response);
        }

        return;
      }

      const updateCart = new CustomEvent("updateCart", {
        detail: {
          cart: response,
          data: options.data,
        },
      });
      document.dispatchEvent(updateCart);

      if (options?.onAfterAdd) {
        options.onAfterAdd(response);
      }
    })
    .catch((error) => {
      if (options?.onError) {
        options.onError(error);
      }

      console.error("Cart action failed:", error);
    });
};

/**
 * Add an item to cart. Once it's in, dl_add_to_cart is pushed for every item
 * carrying `tracking`.
 * @param {Object|Array} items [{ id, quantity, tracking }] — `tracking` as
 *        dataLayer's cartItemTracking builds it, and never sent to the cart
 * @param {Object} options object of callbacks, plus `tracking`: { properties }
 *        for the event and every item — see ADD_TO_CART_SOURCES — or false to
 *        push nothing
 */
window.addToCart = (items, options = {}) => {
  if (options?.onBeforeAdd) {
    options.onBeforeAdd();
  }
  if (!Array.isArray(items)) {
    items = [items];
  }

  const sections = [...document.querySelectorAll("[cart-section-id]")].map(
    (el) => el.getAttribute("cart-section-id"),
  );

  const data = {
    items: items.map(({ tracking, ...item }) => item),
    sections: sections.join(),
  };

  fetch("/cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(data),
  })
    .then((response) => {
      if (response.ok) {
        return response.json();
      }

      return response.json().then((errorDetails) => {
        return Promise.reject(errorDetails);
      });
    })
    .then((response) => {
      const sections = response.sections;

      // Tracking must never fail the add itself.
      try {
        if (options.tracking !== false) {
          trackAddToCart(items, options.tracking?.properties);
        }
      } catch (error) {
        console.warn("Unable to track add to cart", error);
      }

      fetch("/cart.js", {
        headers: {
          "Content-Type": "application/json",
        },
      })
        .then((response) => response.json())
        .then((response) => {
          const updateCart = new CustomEvent("updateCart", {
            detail: {
              cart: { ...response, sections },
              data: options.data,
            },
          });
          document.dispatchEvent(updateCart);
        });

      if (options?.onAfterAdd) {
        options.onAfterAdd(response);
      }
    })
    .catch((error) => {
      if (options?.onError) {
        options.onError(error);
      }

      console.error("Cart action failed:", error);
    });
};
